import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

interface DemoModeContextValue {
  isDemoEnabled: boolean;
  setDemoEnabled: (enabled: boolean) => void;
}

const STORAGE_KEY = "demo-mode";
const DEFAULT_ENABLED = true;

// localStorage can throw in private mode, some embedded contexts, or when
// storage is disabled. Fail open to the default rather than crashing the app.
const getStoredEnabled = (): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "on") return true;
    if (stored === "off") return false;
  } catch {
    // ignore — fall through to default
  }
  return DEFAULT_ENABLED;
};

const persistEnabled = (enabled: boolean): void => {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "on" : "off");
  } catch {
    // ignore — user's choice won't persist but the session still works
  }
};

const DemoModeContext = createContext<DemoModeContextValue>({
  isDemoEnabled: DEFAULT_ENABLED,
  setDemoEnabled: () => {},
});

interface DemoModeProviderProps {
  children: ReactNode;
}

export const DemoModeProvider: FC<DemoModeProviderProps> = ({ children }) => {
  const [isDemoEnabled, setEnabledState] = useState<boolean>(getStoredEnabled);

  const setDemoEnabled = useCallback((enabled: boolean) => {
    setEnabledState(enabled);
    persistEnabled(enabled);
  }, []);

  const value = useMemo(
    () => ({ isDemoEnabled, setDemoEnabled }),
    [isDemoEnabled, setDemoEnabled],
  );

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
};

export const useDemoMode = (): DemoModeContextValue =>
  useContext(DemoModeContext);
